import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Spade,
  Users,
  Coins,
  Wallet,
  CircleCheck as CheckCircle2,
  Clock,
  User,
  Phone,
  LoaderCircle as Loader2,
  CircleAlert as AlertCircle,
  Sparkles,
  MapPin,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getGamePublic, claimSeatPublic } from '../lib/functions';

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

const CURRENCY = { ILS: '₪', NIS: '₪', USD: '$', EUR: '€', GBP: '£' };
const sym = (c) => CURRENCY[c] || c || '₪';
const firstName = (name = '') => String(name).trim().split(/\s+/)[0] || name;
const money = (amount, currency) =>
  `${sym(currency)}${Number(amount || 0).toLocaleString('he-IL')}`;

const isWaitlisted = (s) =>
  ['waitlist', 'waiting', 'queued', 'pending'].includes(String(s || '').toLowerCase());

/* ------------------------------------------------------------------ */
/* seat dot row                                                       */
/* ------------------------------------------------------------------ */

const SeatDots = ({ seats = 0, taken = 0 }) => {
  const total = Math.max(0, Number(seats) || 0);
  return (
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: total }).map((_, i) => {
        const filled = i < taken;
        return (
          <span
            key={i}
            className={`h-2.5 w-2.5 rounded-full transition-colors ${
              filled
                ? 'bg-felt-500 shadow-[0_0_8px_rgba(18,165,102,0.6)]'
                : 'bg-white/10 border border-white/10'
            }`}
          />
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* full-screen states                                                 */
/* ------------------------------------------------------------------ */

const Shell = ({ children }) => (
  <div className="flex min-h-screen w-full items-center justify-center px-4 py-10">
    <div className="w-full max-w-md">{children}</div>
  </div>
);

const LoadingState = () => (
  <Shell>
    <div className="card-premium flex flex-col items-center gap-4 p-14">
      <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
      <p className="text-sm text-slate-400">טוענים את פרטי המשחק…</p>
    </div>
  </Shell>
);

const NotFoundState = ({ message }) => (
  <Shell>
    <div className="card-premium p-10 text-center">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
        <AlertCircle className="h-8 w-8 text-slate-400" />
      </div>
      <h2 className="text-xl font-display font-bold text-slate-100">
        לא מצאנו את המשחק
      </h2>
      <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-400">
        {message ||
          'הקישור אינו תקין או שהמשחק כבר נסגר. בקשו מהמארח קישור מעודכן.'}
      </p>
    </div>
  </Shell>
);

/* ------------------------------------------------------------------ */
/* main component                                                     */
/* ------------------------------------------------------------------ */

export const JoinGame = () => {
  const { code } = useParams();
  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [result, setResult] = useState(null); // { status, seat, already }

  /* load game ------------------------------------------------------ */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!code) {
        setLoadError('missing');
        setLoading(false);
        return;
      }
      try {
        const res = await getGamePublic({ joinCode: code });
        if (!alive) return;
        if (!res || !res.name) {
          setLoadError('notfound');
        } else {
          setGame(res);
        }
      } catch {
        if (!alive) return;
        setLoadError('notfound');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [code]);

  /* derived -------------------------------------------------------- */
  const roster = useMemo(
    () => (Array.isArray(game?.roster) ? game.roster : []),
    [game]
  );
  const seated = roster.filter((r) => !isWaitlisted(r.status));
  const waiting = roster.filter((r) => isWaitlisted(r.status));

  /* submit --------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const cleanName = name.trim();
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    if (cleanName.length < 2) {
      setFormError('אנא הזינו שם מלא');
      return;
    }
    if (cleanPhone.replace(/\D/g, '').length < 7) {
      setFormError('אנא הזינו מספר טלפון תקין');
      return;
    }
    setSubmitting(true);
    try {
      const res = await claimSeatPublic({
        joinCode: code,
        name: cleanName,
        phone: cleanPhone,
      });
      setResult(res || { status: 'confirmed' });
    } catch {
      setFormError('שריון המקום נכשל. נסו שוב בעוד רגע.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------- */
  if (loading) return <LoadingState />;
  if (loadError || !game)
    return (
      <NotFoundState
        message={
          loadError === 'missing'
            ? 'לא צורף קוד הצטרפות לקישור.'
            : undefined
        }
      />
    );

  const { name: gameName, hostName, stakes = {}, minBuyIn, seats, taken, free } =
    game;
  const takenCount = taken ?? seated.length;
  const isConfirmed = !isWaitlisted(result?.status);

  /* success screen ------------------------------------------------- */
  if (result) {
    return (
      <div dir={isRtl ? 'rtl' : 'ltr'}>
        <Shell>
          <div
            className="card-premium relative overflow-hidden p-8 text-center sm:p-10 animate-pulseGlow"
            style={{
              background: isConfirmed
                ? 'linear-gradient(180deg, rgba(12,127,78,0.22) 0%, rgba(11,15,26,0.94) 70%)'
                : 'linear-gradient(180deg, rgba(212,175,55,0.16) 0%, rgba(11,15,26,0.94) 70%)',
              borderColor: isConfirmed
                ? 'rgba(47,190,130,0.35)'
                : 'rgba(212,175,55,0.35)',
            }}
          >
            <div
              className={`mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border animate-floaty ${
                isConfirmed
                  ? 'border-felt-400/40 bg-felt-500/15'
                  : 'border-gold-400/40 bg-gold-500/15'
              }`}
            >
              {isConfirmed ? (
                <CheckCircle2 className="h-10 w-10 text-felt-400" />
              ) : (
                <Clock className="h-10 w-10 text-gold-400" />
              )}
            </div>

            {isConfirmed ? (
              <>
                <h2 className="text-2xl font-display font-bold text-slate-50">
                  אתם בפנים! 🃏
                </h2>
                {result?.seat != null && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-felt-500/30 bg-felt-500/10 px-4 py-1.5 text-sm font-semibold text-felt-50">
                    <MapPin className="h-4 w-4 text-felt-400" />
                    מושב {result.seat}
                  </div>
                )}
                <p className="mx-auto mt-4 max-w-xs text-sm leading-relaxed text-slate-300">
                  {result?.already
                    ? 'כבר שריינתם מקום למשחק הזה — נתראה בשולחן.'
                    : `שמרנו לכם מקום ב"${gameName}". נתראה בשולחן!`}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-display font-bold text-slate-50">
                  אתם ברשימת ההמתנה
                </h2>
                <p className="mx-auto mt-4 max-w-xs text-sm leading-relaxed text-slate-300">
                  השולחן מלא כרגע, אבל שמרנו את מקומכם בתור. נעדכן אתכם ברגע
                  שמתפנה מושב.
                </p>
              </>
            )}
          </div>
        </Shell>
      </div>
    );
  }

  /* main join screen ----------------------------------------------- */
  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className="min-h-screen w-full px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-md space-y-5">
        {/* Game card */}
        <div className="card-premium relative overflow-hidden p-6 sm:p-7">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-70"
            style={{
              background:
                'radial-gradient(380px 150px at 50% -30%, rgba(212,175,55,0.16), transparent 70%)',
            }}
          />
          <div className="relative">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-gold-500/30 bg-gold-500/10 px-3 py-1 text-[11px] font-semibold text-gold-300">
                  <Sparkles className="h-3 w-3" />
                  הזמנה למשחק
                </div>
                <h1 className="truncate text-2xl font-display font-bold tracking-tight text-slate-50">
                  {gameName}
                </h1>
                {hostName && (
                  <p className="mt-1 text-sm text-slate-400">
                    מארח: <span className="text-slate-200">{hostName}</span>
                  </p>
                )}
              </div>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-felt-500/12 border border-felt-500/30">
                <Spade className="h-6 w-6 text-felt-400" />
              </div>
            </div>

            {/* stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <Coins className="h-3.5 w-3.5 text-gold-400" />
                  בליינדים
                </div>
                <div dir="ltr" className="text-lg font-bold text-slate-100 tabular-nums">
                  {sym(stakes.currency)}
                  {stakes.sb}/{sym(stakes.currency)}
                  {stakes.bb}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <Wallet className="h-3.5 w-3.5 text-gold-400" />
                  כניסה מינימלית
                </div>
                <div className="text-lg font-bold text-slate-100 tabular-nums">
                  {money(minBuyIn, stakes.currency)}
                </div>
              </div>
            </div>

            {/* seats */}
            <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm text-slate-300">
                  <Users className="h-4 w-4 text-felt-400" />
                  <span>
                    <span className="font-bold text-slate-100 tabular-nums">
                      {takenCount}
                    </span>{' '}
                    מתוך{' '}
                    <span className="font-bold text-slate-100 tabular-nums">
                      {seats}
                    </span>{' '}
                    מקומות תפוסים
                  </span>
                </div>
                {free > 0 ? (
                  <span className="chip-live">{free} פנויים</span>
                ) : (
                  <span className="chip-upcoming">רשימת המתנה</span>
                )}
              </div>
              <SeatDots seats={seats} taken={takenCount} />
            </div>

            {/* roster */}
            {(seated.length > 0 || waiting.length > 0) && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  מי כבר בפנים
                </p>
                <div className="flex flex-wrap gap-2">
                  {seated.map((p, i) => (
                    <span key={`s-${i}`} className="chip-live">
                      <User className="h-3 w-3" />
                      {firstName(p.name)}
                    </span>
                  ))}
                  {waiting.map((p, i) => (
                    <span key={`w-${i}`} className="chip-upcoming">
                      <Clock className="h-3 w-3" />
                      {firstName(p.name)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Join form */}
        <form onSubmit={handleSubmit} className="card-premium p-6 sm:p-7">
          <h2 className="mb-1 text-lg text-slate-100">שריינו את המקום שלכם</h2>
          <p className="mb-5 text-sm text-slate-400">
            {free > 0
              ? 'ממלאים פרטים וזהו — נתראה בשולחן.'
              : 'השולחן מלא, אך תוכלו להצטרף לרשימת ההמתנה.'}
          </p>

          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-400">
                שם מלא
              </label>
              <div className="relative">
                <User
                  className={`pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 ${
                    isRtl ? 'right-3.5' : 'left-3.5'
                  }`}
                />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ישראל ישראלי"
                  className={`field ${isRtl ? 'pr-10' : 'pl-10'}`}
                  autoComplete="name"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-400">
                טלפון
              </label>
              <div className="relative">
                <Phone
                  className={`pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 ${
                    isRtl ? 'right-3.5' : 'left-3.5'
                  }`}
                />
                <input
                  type="tel"
                  dir="ltr"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="050-123-4567"
                  className={`field text-left ${isRtl ? 'pr-10' : 'pl-10'}`}
                  autoComplete="tel"
                />
              </div>
            </div>
          </div>

          {formError && (
            <div className="mt-4 inline-flex w-full items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-felt mt-5 w-full text-base"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Spade className="h-5 w-5" />
            )}
            {submitting
              ? 'שומרים מקום…'
              : free > 0
              ? 'שריינו מקום'
              : 'הצטרפו לרשימת ההמתנה'}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">
            בלחיצה אתם מאשרים שקיבלתם הזמנה מהמארח.
          </p>
        </form>
      </div>
    </div>
  );
};

export default JoinGame;
